import {
  BellIcon,
  CheckSquareIcon,
  DatabaseIcon,
  LayoutDashboardIcon,
  ShieldIcon,
  type LucideIcon,
} from "lucide-react";

import { routes } from "@/config/routes";
import type messages from "@/messages/en-US.json";
import type { Permission } from "@/types/auth";

// docs/specs/foundation/navigation.md (FND-15): the one registry for the sidebar, the command
// menu and page titles. Entries are added by the module PR that builds the page (rule 5): no
// placeholders, no links to unbuilt pages. The integrity tests in nav.test.ts keep it honest.

/** A key under "nav" in the message catalogs (src/messages). */
export type NavTitle = keyof (typeof messages)["nav"];

export type NavEntry = {
  /** Shown as t(`nav.${title}`). */
  title: NavTitle;
  /** From `routes`; must be a real page (route integrity test). */
  href: string;
  /** Rule 3: hidden from users without it. Display only: the page checks again (rule 7). */
  capability?: Permission;
  /** Rule 4: shown if the user has any one of these. */
  anyOf?: readonly Permission[];
};

/** Rule 3: sections have no capability; one shows when at least one entry does. */
export type NavSection = {
  title: NavTitle;
  icon: LucideIcon;
  entries: readonly NavEntry[];
};

/** Rule 2: PRD §9 order. Sections without a built page aren't listed yet. */
export const navSections: readonly NavSection[] = [
  {
    title: "dashboard",
    icon: LayoutDashboardIcon,
    entries: [{ title: "dashboard", href: routes.dashboard() }],
  },
  {
    title: "approvals",
    icon: CheckSquareIcon,
    // Modules add their `<resource>:approve` capabilities here as they ship
    entries: [{ title: "inbox", href: routes.approvals(), anyOf: ["approval:decide"] }],
  },
  {
    title: "notifications",
    icon: BellIcon,
    entries: [{ title: "notifications", href: routes.notifications() }],
  },
  {
    title: "masterData",
    icon: DatabaseIcon,
    entries: [
      {
        title: "companyStructure",
        href: routes.companyStructure(),
        capability: "company_structure:read",
      },
      { title: "departments", href: routes.departments(), capability: "department:read" },
      { title: "units", href: routes.units(), capability: "uom:read" },
      { title: "items", href: routes.items(), capability: "item:read" },
      { title: "categories", href: routes.categories(), capability: "item:read" },
      { title: "lots", href: routes.lots(), capability: "lot:read" },
    ],
  },
  {
    title: "administration",
    icon: ShieldIcon,
    entries: [
      { title: "users", href: routes.users(), capability: "user:read" },
      { title: "roles", href: routes.roles(), capability: "role:read" },
      {
        title: "approvalWorkflows",
        href: routes.approvalWorkflows(),
        capability: "approval_policy:read",
      },
      { title: "auditLog", href: routes.auditLog(), capability: "audit:read" },
    ],
  },
];

export const canSeeEntry = (entry: NavEntry, permissions: readonly Permission[]): boolean =>
  (!entry.capability || permissions.includes(entry.capability)) &&
  (!entry.anyOf || entry.anyOf.some((p) => permissions.includes(p)));

/** The sections and entries this user may see. Display only: hiding a link isn't access control. */
export function visibleNav(permissions: readonly Permission[]): NavSection[] {
  return navSections.flatMap((section) => {
    const entries = section.entries.filter((entry) => canSeeEntry(entry, permissions));
    return entries.length > 0 ? [{ ...section, entries }] : [];
  });
}

/** A section with one entry is a plain link in the sidebar, under the section's name. */
export const isSingleLink = (section: NavSection): boolean => section.entries.length === 1;

const titledLinks = navSections.flatMap((section) =>
  section.entries.map((entry) => ({
    href: entry.href,
    title: isSingleLink(section) ? section.title : entry.title,
  })),
);

/**
 * Rule 11: the nav title key for a path, by longest-prefix match; detail pages
 * (/administration/users/123) get their list's title.
 */
export function navTitleFor(pathname: string): NavTitle | undefined {
  let best: { href: string; title: NavTitle } | undefined;
  for (const link of titledLinks) {
    const matches = pathname === link.href || pathname.startsWith(`${link.href}/`);
    if (matches && (!best || link.href.length > best.href.length)) best = link;
  }
  return best?.title;
}

/** The registry entry whose page this path is (or is a detail page of), for highlighting. */
export function activeHref(pathname: string): string | undefined {
  let best: string | undefined;
  for (const { href } of titledLinks) {
    const matches = pathname === href || pathname.startsWith(`${href}/`);
    if (matches && (!best || href.length > best.length)) best = href;
  }
  return best;
}
